import type {
  CogoApp,
  CogoBlockDef,
  CogoBlockManifest,
  CogoElement,
  CogoFlow,
} from "@cogo/react-renderer/core/types";
import {
  validateNamespaces,
  resolveBindings,
} from "@cogo/react-renderer/runtime/namespace-validator";
import {
  loadDesignTokens,
  type DesignTokens,
} from "@cogo/react-renderer/runtime/design-tokens";
import { expandDefinitions } from "@cogo/react-renderer/core/ref-expander";
import { resolveTailwindToStyles } from "@cogo/schema/tailwind-resolver";
import type { App as AppV2, BlockDef as BlockDefV2 } from "@cogo/types-v2";
import { loadBlock as loadBlockV2, V2_FILES } from "@cogo/runtime-v2";
import { tryDeriveThemeTokensFromDesignSystem } from "@cogo/design-presets";
import {
  decodeRenderPackPublishPayload,
  renderPackWireBytesToUint8Array,
} from "@cogo/iframe-protocol";
import { fetchPresetTokens } from "./app-loader-preset";

export interface LoadedApp {
  manifest: CogoApp;
  blocks: Record<string, CogoBlockDef>;
  data: Record<string, unknown>;
  flows: Record<string, CogoFlow>;
  tokens?: DesignTokens;
  renderPackBytes?: Record<string, Uint8Array>;
}

export interface ThemeTokens {
  colors?: Record<string, string>;
  radius?: string;
  /**
   * Font stacks emitted as --font-sans / --font-mono / --font-heading via
   * getIsolatedAppStyle(). Populated by the design-system.json::axes path
   * (see tryDeriveThemeTokensFromDesignSystem in @cogo/design-presets).
   */
  fonts?: { sans?: string; mono?: string; heading?: string };
}

export interface LoadedAppV2 {
  manifest: AppV2;
  blocks: Record<string, BlockDefV2>;
  schemaVersion: "3.0";
  designSystem?: Record<string, string>;
  themeTokens?: ThemeTokens;
  renderPackBytes?: Record<string, Uint8Array>;
}

/**
 * Generated v2 fixtures historically mixed root global-state bindings with
 * canonical `appData.*` expressions. The runtime intentionally keeps those
 * namespaces distinct, so a valid default could still render as an empty
 * collection. Normalize that producer drift once at the app-loading boundary:
 * every declared global default is readable through appData, while explicit
 * appData remains authoritative.
 */
export function normalizeManifestAppData(manifest: AppV2): AppV2 {
  const globalState = manifest.globalState ?? {};
  const nestedAppData = globalState.appData?.default;
  const defaults = Object.fromEntries(
    Object.entries(globalState)
      .filter(
        ([key, definition]) =>
          key !== "appData" &&
          definition !== null &&
          typeof definition === "object" &&
          "default" in definition,
      )
      .map(([key, definition]) => [
        key,
        (definition as { default?: unknown }).default,
      ]),
  );
  const appData = {
    ...(nestedAppData !== null &&
    typeof nestedAppData === "object" &&
    !Array.isArray(nestedAppData)
      ? (nestedAppData as Record<string, unknown>)
      : {}),
    ...defaults,
    ...(manifest.appData ?? {}),
  };

  return { ...manifest, appData };
}

/** Guard: only parse response as JSON if content-type is application/json */
function isJsonResponse(res: Response): boolean {
  const ct = res.headers.get("content-type") ?? "";
  return ct.includes("application/json");
}

export async function loadApp(basePath: string): Promise<LoadedApp> {
  const manifestRes = await fetch(`${basePath}/app.cogo.json`);
  if (!manifestRes.ok)
    throw new Error(`Failed to load manifest: ${manifestRes.status}`);
  const manifest: CogoApp = await manifestRes.json();

  const blocks: Record<string, CogoBlockDef> = {};
  const flows: Record<string, CogoFlow> = {};

  // Collect unique block names across all pages + global logic blocks
  const blockNames = new Set<string>();
  if (manifest.globalBlocks) {
    for (const blockName of manifest.globalBlocks) {
      blockNames.add(blockName);
    }
  }
  for (const page of manifest.pages) {
    for (const blockName of page.blocks) {
      blockNames.add(blockName);
    }
  }

  // Load blocks iteratively — discover sub-blocks referenced via block-ref
  const loaded = new Set<string>();
  while (blockNames.size > loaded.size) {
    const toLoad = Array.from(blockNames).filter((n) => !loaded.has(n));
    if (toLoad.length === 0) break;

    await Promise.all(
      toLoad.map(async (blockName) => {
        loaded.add(blockName);
        await loadBlock(basePath, blockName, blocks, flows, blockNames);
      }),
    );
  }

  // Validate logic block namespaces (after all blocks loaded)
  const nsMap = validateNamespaces(blocks);
  const bindingsMap = resolveBindings(blocks, nsMap);

  // Load static datasources from app manifest
  const data: Record<string, unknown> = {};
  await Promise.all(
    Object.entries(manifest.datasources).map(async ([name, ds]) => {
      if (ds.type === "static" && ds.path) {
        try {
          const dsUrl = ds.path.startsWith("/")
            ? `${basePath}${ds.path}`
            : `${basePath}/${ds.path}`;
          const res = await fetch(dsUrl);
          if (res.ok && isJsonResponse(res)) {
            data[name] = await res.json();
          }
        } catch (err) {
          console.warn(`Failed to load datasource: ${name}`, err);
        }
      }
    }),
  );

  // Load design tokens from theme.json (optional)
  let tokens: DesignTokens | undefined;
  const themePath = (manifest as unknown as Record<string, unknown>)
    .themePath as string | undefined;
  if (themePath) {
    try {
      const themeRes = await fetch(`${basePath}/${themePath}`);
      if (themeRes.ok && isJsonResponse(themeRes)) {
        const raw = await themeRes.json();
        tokens = loadDesignTokens(raw);
      }
    } catch {
      // theme.json is optional — fall back to defaults
    }
  }

  return { manifest, blocks, data, flows, tokens };
}

/**
 * Load app from a single pre-bundled schema URL (tenant mode).
 * The schema JSON must contain: { manifest, blocks, flows?, data?, tokens? }
 * This avoids N+1 fetch calls — everything in one response.
 */
export async function loadFromSchemaUrl(schemaUrl: string): Promise<LoadedApp> {
  const res = await fetch(schemaUrl);
  if (!res.ok) throw new Error(`Failed to load schema: ${res.status}`);
  const schema = await res.json();

  // Pre-bundled format: { manifest, blocks, flows, data, tokens }
  if (schema.manifest && schema.blocks) {
    const blocks = schema.blocks as Record<string, CogoBlockDef>;
    for (const block of Object.values(blocks)) {
      if (block.root) enrichElement(block.root);
    }
    return {
      manifest: schema.manifest,
      blocks,
      data: schema.data ?? {},
      flows: schema.flows ?? {},
      tokens: schema.tokens,
      renderPackBytes: renderPackWireBytesToUint8Array(
        decodeRenderPackPublishPayload(schema),
      ),
    };
  }

  throw new Error("Schema must be in bundled format { manifest, blocks }");
}

/**
 * Load all 5 block files:
 * 1. ui.json       — element tree (required)
 * 2. flow.json     — action flows (optional)
 * 3. state.json    — state + computed (optional)
 * 4. events.json   — event bindings (optional)
 * 5. data.json     — data sources (optional)
 *
 * Falls back to .cogo.json for legacy blocks.
 */
async function loadBlock(
  basePath: string,
  blockName: string,
  blocks: Record<string, CogoBlockDef>,
  flows: Record<string, CogoFlow>,
  blockNames: Set<string>,
): Promise<void> {
  const blockPath = `${basePath}/blocks/${blockName}`;

  /** Fetch JSON with fallback: try .json first, then .cogo.json */
  async function fetchJson(
    name: string,
    legacyName?: string,
  ): Promise<any | null> {
    try {
      const res = await fetch(`${blockPath}/${name}`);
      if (res.ok && isJsonResponse(res)) return res.json();
    } catch {
      /* ignore */
    }
    if (legacyName) {
      try {
        const res = await fetch(`${blockPath}/${legacyName}`);
        if (res.ok && isJsonResponse(res)) return res.json();
      } catch {
        /* ignore */
      }
    }
    return null;
  }

  // 0. block manifest (optional)
  let blockManifest: CogoBlockManifest | undefined;
  const manifestData = await fetchJson("meta.json", "block.cogo.json");
  if (manifestData) blockManifest = manifestData;

  // 1. ui (required)
  const uiData = await fetchJson("ui.json", "ui.cogo.json");
  if (!uiData) {
    console.warn(`Block UI not found: ${blockName}`);
    return;
  }

  const blockDef: CogoBlockDef = expandDefinitions(uiData);
  enrichElement(blockDef.root);
  blocks[blockName] = blockDef;

  if (blockManifest) {
    blocks[blockName].manifest = blockManifest;
  }

  walkElements(blockDef.root, (el) => {
    if (
      el.type === "block-ref" &&
      el.blockType &&
      !blockNames.has(el.blockType)
    ) {
      blockNames.add(el.blockType);
    }
  });

  // 2. flows (optional) — namespaced per block
  const flowData = await fetchJson("flow.json", "flow.cogo.json");
  if (flowData) {
    const flowEntries = flowData.flows
      ? Object.entries(flowData.flows)
      : Object.entries(flowData).filter(
          ([, v]) =>
            v &&
            typeof v === "object" &&
            (v as Record<string, unknown>).trigger,
        );
    for (const [flowName, flow] of flowEntries) {
      const ns = blockManifest?.namespace ?? blockName;
      const namespacedKey = `${ns}.${flowName}`;
      flows[namespacedKey] = flow as CogoFlow;
      flows[flowName] = flow as CogoFlow;
    }
  }

  // 3. state/variables (optional)
  const varData = await fetchJson("state.json", "variables.cogo.json");
  if (varData && blocks[blockName]) {
    blocks[blockName].variables = varData;
  }

  // 4. events (optional)
  const evtData = await fetchJson("events.json", "events.cogo.json");
  if (evtData && blocks[blockName]) {
    blocks[blockName].events = evtData;
  }

  // 5. data/datasource (optional)
  const dsData = await fetchJson("data.json", "datasource.cogo.json");
  if (dsData && blocks[blockName]) {
    blocks[blockName].datasource = dsData;
  }

  // 6. manifest.sig verification (optional — infrastructure for Phase 3)
  try {
    const sigRes = await fetch(`${blockPath}/manifest.sig`);
    if (sigRes.ok && isJsonResponse(sigRes)) {
      const manifest = await sigRes.json();
      if (blocks[blockName]) {
        (blocks[blockName] as any).__manifestSig = manifest;
      }
      console.debug(`[cogo] Block "${blockName}" signature loaded`);
    }
  } catch {
    // manifest.sig is optional in development
  }
}

function walkElements(
  element: CogoElement,
  fn: (el: CogoElement) => void,
): void {
  fn(element);
  element.children?.forEach((child) => walkElements(child, fn));
}

/**
 * Enrich an element tree with resolvedStyles by converting className
 * to platform-agnostic style values. Skips elements that already have
 * resolvedStyles (e.g., pre-built blocks).
 */
export function enrichElement(el: CogoElement): CogoElement {
  if (el.className && !el.resolvedStyles) {
    el.resolvedStyles = resolveTailwindToStyles(el.className);
  }
  if (el.children) {
    el.children = el.children.map(enrichElement);
  }
  return el;
}

// ── v2 App Loading ─────────────────────────────────────────────

/**
 * Detect schema version for an app directory.
 * v2 apps use app.json with schemaVersion "3.0".
 * v1 apps use app.cogo.json.
 */
export async function detectSchemaVersion(
  basePath: string,
): Promise<"3.0" | "1.0"> {
  try {
    const res = await fetch(`${basePath}/app.json`);
    if (res.ok && isJsonResponse(res)) {
      const manifest = await res.json();
      if (manifest.schemaVersion === "3.0") return "3.0";
    }
  } catch {
    // fall through to v1
  }
  return "1.0";
}

/** Fetch-based FileReader for v2 block loader */
function fetchFileReader(url: string): Promise<unknown> {
  return fetch(url).then((res) => {
    if (!res.ok) throw new Error(`${res.status}`);
    const ct = res.headers.get("content-type") ?? "";
    // Accept application/json and text/* (CF Pages may serve .json as text/plain)
    if (ct.includes("application/json") || ct.includes("text/")) {
      return res.json();
    }
    // Try parsing as JSON anyway — some CDNs return unexpected content-types for .json files
    return res
      .clone()
      .json()
      .catch(() => {
        throw new Error("Not JSON");
      });
  });
}

/**
 * Load a v2 app — app.json + v2 block files (meta.json, ui.json, etc.)
 */
export async function loadAppV2(basePath: string): Promise<LoadedAppV2> {
  const manifestRes = await fetch(`${basePath}/app.json`);
  if (!manifestRes.ok)
    throw new Error(`Failed to load v2 manifest: ${manifestRes.status}`);
  const manifest = normalizeManifestAppData(
    (await manifestRes.json()) as AppV2,
  );
  const renderPackBytes = renderPackWireBytesToUint8Array(
    decodeRenderPackPublishPayload(manifest),
  );

  const blocks: Record<string, BlockDefV2> = {};

  // Collect all block names from pages + globalBlocks
  const blockNames = new Set<string>();
  for (const blockName of manifest.globalBlocks ?? [])
    blockNames.add(blockName);
  for (const page of manifest.pages) {
    for (const blockName of page.blocks) blockNames.add(blockName);
  }

  // Load all v2 blocks in parallel
  await Promise.all(
    Array.from(blockNames).map(async (blockName) => {
      try {
        const blockDef = await loadBlockV2(
          `${basePath}/blocks/${blockName}`,
          fetchFileReader,
        );
        blocks[blockName] = blockDef;
      } catch (err) {
        console.warn(`[v2] Failed to load block: ${blockName}`, err);
      }
    }),
  );

  // Guard: if ALL blocks failed to load, the app will render blank — throw instead of silently continuing
  const loadedCount = Object.keys(blocks).length;
  if (loadedCount === 0 && blockNames.size > 0) {
    throw new Error(
      `All ${blockNames.size} blocks failed to load from ${basePath}`,
    );
  }
  if (loadedCount < blockNames.size) {
    console.warn(
      `[v2] Loaded ${loadedCount}/${blockNames.size} blocks — some may render blank`,
    );
  }

  // Load design-system tokens — three sources, in priority order:
  //   0. design_system_preset_id (Phase 600) — fetch preset.tokens from the
  //      platform Worker.  Requires a valid Supabase JWT; silently skipped when
  //      the user is not logged in or the manifest field is absent.
  //   1. design-system.json — static asset written by Studio Save (since
  //      2026-04-16).  Used when no preset ID is present.
  //   2. (none) — designSystemRaw remains undefined; downstream axes path
  //      falls through to the legacy themePath fallback below.
  let designSystem: Record<string, string> | undefined;
  let designSystemRaw: unknown;

  // Priority 0: design_system_preset_id → Worker fetch
  const presetId = (manifest as unknown as Record<string, unknown>)
    .design_system_preset_id as string | undefined;
  if (presetId && typeof presetId === "string") {
    let jwt: string | null = null;
    try {
      const { supabase } = await import("./lib/supabase");
      const { data } = await supabase.auth.getSession();
      jwt = data.session?.access_token ?? null;
    } catch {
      // supabase unavailable (e.g. server-side / test context)
      jwt = null;
    }
    const presetTokens = await fetchPresetTokens(presetId, jwt);
    if (presetTokens !== null) {
      designSystemRaw = presetTokens;
      designSystem = presetTokens as Record<string, string>;
    }
  }

  // Priority 1: static design-system.json (optional)
  if (!designSystemRaw) {
    try {
      const dsRes = await fetch(`${basePath}/design-system.json`);
      if (dsRes.ok && isJsonResponse(dsRes)) {
        designSystemRaw = await dsRes.json();
        // Preserve full payload for axes lookup; expose the flat utility-class
        // map (sans the structured `axes`/`colors`/`radius`/`components` keys)
        // as `designSystem` so existing call sites that index by name still
        // find string utility classes only.
        designSystem = designSystemRaw as Record<string, string>;
      }
    } catch {
      // design-system.json is optional
    }
  }

  // Theme tokens — two sources, in priority order:
  //   1. design-system.json::axes (Studio Save flow output, since 2026-04-16)
  //      — derived via @cogo/design-presets::lookupPreset
  //   2. themePath JSON (legacy `resources/theme-default.json`) — used by all
  //      pre-axes apps; remains the silent fallback so 53 existing templates
  //      render unchanged.
  let themeTokens: ThemeTokens | undefined;
  const axesTokens = tryDeriveThemeTokensFromDesignSystem(designSystemRaw);
  if (axesTokens) {
    themeTokens = axesTokens;
  } else {
    const themePath = (manifest as unknown as Record<string, string>).themePath;
    if (themePath) {
      try {
        const themeRes = await fetch(`${basePath}/${themePath}`);
        if (themeRes.ok && isJsonResponse(themeRes)) {
          themeTokens = await themeRes.json();
        }
      } catch {
        // theme JSON is optional
      }
    }
  }

  return {
    manifest,
    blocks,
    schemaVersion: "3.0",
    designSystem,
    themeTokens,
    renderPackBytes,
  };
}
